import { HttpClient } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { firstValueFrom } from "rxjs";
import { environment } from "../../../../Environments/Environment";
import { PrintingSearchRequest } from "../Dto/PrintingSearchRequest";
import { PrintingSearchResponse } from "../Dto/PrintingSearchResponse";
import { CreatePrintingRequest, UpdatePrintingRequest, InspectPrintingRequest } from "../Dto/CreatePrintingRequest";
import { PrintingEntity } from "../Dto/PrintingModel";
import { sortByMaterialSequence } from "../../../Common/Utility/UtilityMaterialSequence";

@Injectable({ providedIn: "root" })
export class PrintingApi {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = environment.adminServiceUrl;

  async searchPrintings(params: PrintingSearchRequest): Promise<PrintingSearchResponse> {
    const url = new URL("printings", `${this.baseUrl}/`);
    const searchParams = new URLSearchParams();

    if (params.search) searchParams.set("search", params.search);
    if (params.vendorId) searchParams.set("vendorId", params.vendorId);
    if (params.warehouseId) searchParams.set("warehouseId", params.warehouseId);
    if (params.status) searchParams.set("status", params.status);
    if (params.page !== undefined) searchParams.set("page", params.page.toString());
    if (params.limit !== undefined) searchParams.set("limit", params.limit.toString());
    if (params.createdAtFrom) searchParams.set("createdAtFrom", params.createdAtFrom);
    if (params.createdAtTo) searchParams.set("createdAtTo", params.createdAtTo);

    if (searchParams.toString()) {
      url.search = searchParams.toString();
    }

    const response = await firstValueFrom(this.http.get<PrintingSearchResponse>(url.toString(), { withCredentials: true }));
    return {
      ...response,
      data: response.data.map((item) => this.normalizePrinting(item)),
    };
  }

  async createPrinting(payload: CreatePrintingRequest): Promise<PrintingEntity> {
    const url = new URL("printings", `${this.baseUrl}/`);
    const response = await firstValueFrom(this.http.post<PrintingEntity>(url.toString(), payload, { withCredentials: true }));
    return this.normalizePrinting(response);
  }

  async updatePrinting(id: string, payload: UpdatePrintingRequest): Promise<PrintingEntity> {
    const url = new URL(`printings/${id}`, `${this.baseUrl}/`);
    const response = await firstValueFrom(this.http.put<PrintingEntity>(url.toString(), payload, { withCredentials: true }));
    return this.normalizePrinting(response);
  }

  async getPrintingById(id: string): Promise<PrintingEntity | null> {
    const url = new URL(`printings/${id}`, `${this.baseUrl}/`);
    const response = await firstValueFrom(this.http.get<PrintingEntity>(url.toString(), { withCredentials: true }));
    return this.normalizePrinting(response);
  }

  async confirmPrinting(id: string): Promise<PrintingEntity> {
    const url = new URL(`printings/${id}/confirm`, `${this.baseUrl}/`);
    const response = await firstValueFrom(this.http.post<PrintingEntity>(url.toString(), {}, { withCredentials: true }));
    return this.normalizePrinting(response);
  }

  async shipPrinting(id: string): Promise<PrintingEntity> {
    const url = new URL(`printings/${id}/ship`, `${this.baseUrl}/`);
    const response = await firstValueFrom(this.http.post<PrintingEntity>(url.toString(), {}, { withCredentials: true }));
    return this.normalizePrinting(response);
  }

  async inspectPrinting(id: string, payload: InspectPrintingRequest): Promise<PrintingEntity> {
    const url = new URL(`printings/${id}/inspect`, `${this.baseUrl}/`);
    const response = await firstValueFrom(this.http.post<PrintingEntity>(url.toString(), payload, { withCredentials: true }));
    return this.normalizePrinting(response);
  }

  async deletePrinting(id: string): Promise<void> {
    const url = new URL(`printings/${id}`, `${this.baseUrl}/`);
    await firstValueFrom(this.http.delete<void>(url.toString(), { withCredentials: true }));
  }

  private normalizePrinting(item: PrintingEntity): PrintingEntity {
    return {
      ...item,
      attachments: item.attachments ?? [],
      items: sortByMaterialSequence(item.items ?? [], (printingItem) => printingItem.material ?? {}),
    };
  }
}
